/*
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved.
* Distributed under the TruVideo proprietary license.
*/
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  BookOpen, PlusCircle, Search, Trash2, X, Building2, AlertTriangle, Filter
} from 'lucide-react';
import {
  knowledgeBaseService, AI_AGENT_ERROR_CODES, getApiErrorMessage
} from '../services/knowledgeBaseService';
import { accountService } from '../services/accountService';
import Modal from '../components/Modal';
import toastService from '../utils/toastService';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../context/PermissionContext';
import AgenticAiUnavailable from '../components/aiAgents/AgenticAiUnavailable';
import { getAgenticAiServiceError } from '../utils/agenticAiErrors';
import { parseTimestamp, formatDate, formatDateTime } from '../utils/agenticAiDates';
import { ACCOUNT_LABEL } from '../constants/entityLabels';

const NEW_ROUTE = '/knowledge-bases/new';

const KnowledgeBases = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { can } = usePermissions();
  const canCreateKnowledgeBase = can.knowledge_bases.create();
  const canDeleteKnowledgeBase = can.knowledge_bases.delete();
  const [searchParams, setSearchParams] = useSearchParams();

  // Platform-wide view picks the account; account / sub-account users are pinned to their own.
  const showAccountField = !user?.accountUID;
  const requestedAccountId = searchParams.get('accountId') || '';

  const [accounts, setAccounts] = useState([]);
  // 'loading' | 'loaded' | 'failed' — the URL account is only trusted once it's known to exist.
  const [accountsStatus, setAccountsStatus] = useState('loading');
  const accountsLoading = showAccountField && accountsStatus === 'loading';
  const isKnownAccount = accountsStatus === 'failed' || accounts.some((a) => a.id === requestedAccountId);
  let selectedAccountId = user?.accountUID;
  if (showAccountField) {
    selectedAccountId = !accountsLoading && requestedAccountId && isKnownAccount ? requestedAccountId : '';
  }
  const [knowledgeBases, setKnowledgeBases] = useState([]);
  const [loading, setLoading] = useState(false);
  const [integrationError, setIntegrationError] = useState(null);
  // Set when the Agentic AI platform itself does not respond properly.
  const [serviceError, setServiceError] = useState(null);
  const [search, setSearch] = useState('');

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!showAccountField) { return; }
    const fetchAccounts = async () => {
      try {
        const { data } = await accountService.getAccounts({ limit: 100 });
        setAccounts([...(data.data || data || [])].sort((a, b) =>
          (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' })));
        setAccountsStatus('loaded');
      } catch {
        setAccountsStatus('failed');
        toastService.error(`Failed to load ${ACCOUNT_LABEL.pluralLower}`);
      }
    };
    fetchAccounts();
  }, [showAccountField]);

  const requestIdRef = useRef(0);
  const fetchKnowledgeBases = async () => {
    const currentRequestId = ++requestIdRef.current;
    setIntegrationError(null);
    setServiceError(null);
    if (!selectedAccountId) {
      setKnowledgeBases([]);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const { data } = await knowledgeBaseService.getKnowledgeBases(
        showAccountField ? { accountId: selectedAccountId } : {}
      );
      if (currentRequestId !== requestIdRef.current) { return; }
      setKnowledgeBases(Array.isArray(data?.data) ? data.data : []);
    } catch (err) {
      if (currentRequestId !== requestIdRef.current) { return; }
      setKnowledgeBases([]);
      const message = getApiErrorMessage(err, 'Failed to load knowledge bases');
      if (getAgenticAiServiceError(err)) {
        setServiceError(getAgenticAiServiceError(err));
      } else if (AI_AGENT_ERROR_CODES.includes(err.response?.data?.code)) {
        setIntegrationError(message);
      } else if (showAccountField && err.response?.status === 404) {
        // Account no longer exists (or wasn't in the loaded list) — same as no account selected.
        handleAccountChange('');
      } else {
        toastService.error(message);
      }
    } finally {
      if (currentRequestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchKnowledgeBases();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAccountId]);

  const handleAccountChange = (accountId) => {
    setSearch('');
    setSearchParams(accountId ? { accountId } : {}, { replace: true });
  };

  // An unknown ?accountId= is treated as "no account selected" (dropped from the URL, no error).
  useEffect(() => {
    if (showAccountField && accountsStatus === 'loaded' && requestedAccountId && !isKnownAccount) {
      setSearchParams({}, { replace: true });
    }
  }, [showAccountField, accountsStatus, requestedAccountId, isKnownAccount, setSearchParams]);

  const handleNew = () => {
    navigate(showAccountField && selectedAccountId ? `${NEW_ROUTE}?accountId=${encodeURIComponent(selectedAccountId)}` : NEW_ROUTE);
  };

  // Documents are listed and uploaded from the knowledge base's documents page.
  const handleViewDocuments = (kb) => {
    const query = showAccountField ? `?accountId=${encodeURIComponent(selectedAccountId)}` : '';
    navigate(`/knowledge-bases/${encodeURIComponent(kb.id)}/documents${query}`, {
      state: { knowledgeBase: { id: kb.id, name: kb.name } },
    });
  };

  const handleDeleteConfirm = async () => {
    try {
      setDeleting(true);
      await knowledgeBaseService.deleteKnowledgeBase(
        deleteTarget.id,
        showAccountField ? { accountId: selectedAccountId } : {}
      );
      toastService.success('Knowledge base deleted successfully');
      setDeleteTarget(null);
      fetchKnowledgeBases();
    } catch (err) {
      toastService.error(getApiErrorMessage(err, 'Failed to delete knowledge base'));
    } finally {
      setDeleting(false);
    }
  };

  const filteredKnowledgeBases = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) { return knowledgeBases; }
    return knowledgeBases.filter((kb) =>
      `${kb.name || ''} ${kb.description || ''}`.toLowerCase().includes(term)
    );
  }, [knowledgeBases, search]);

  const canCreate = Boolean(selectedAccountId) && !integrationError && !serviceError;
  const needsAccount = showAccountField && !selectedAccountId;
  const selectedAccountName = accounts.find(a => a.id === selectedAccountId)?.name || selectedAccountId;
  const hasActiveFilters = Boolean(search || (showAccountField && selectedAccountId));

  const handleClearFilters = () => {
    setSearch('');
    if (showAccountField) { handleAccountChange(''); }
  };

  const renderEmptyState = (icon, title, description, action = null) => (
    <div className="py-16 px-6 flex flex-col items-center justify-center text-center space-y-3">
      <div className="w-14 h-14 rounded-2xl bg-base-200 flex items-center justify-center text-base-content/30 shadow-inner">
        {icon}
      </div>
      <div>
        <h3 className="text-base font-black text-base-content">{title}</h3>
        <p className="text-xs text-base-content/40 max-w-sm mt-1">{description}</p>
      </div>
      {action}
    </div>
  );

  const renderBody = () => {
    if (needsAccount && !(accountsLoading && requestedAccountId)) {
      return renderEmptyState(
        <Building2 size={28} />,
        `Select ${ACCOUNT_LABEL.withArticle}`,
        `Choose ${ACCOUNT_LABEL.withArticle} to view and manage its knowledge bases.`
      );
    }
    if (loading || accountsLoading) {
      return (
        <div className="p-6 space-y-4">
          <div className="h-6 w-32 bg-base-300/60 rounded animate-pulse"></div>
          <div className="space-y-2">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-12 bg-base-200/50 rounded-xl animate-pulse"></div>
            ))}
          </div>
        </div>
      );
    }
    if (serviceError) {
      return <AgenticAiUnavailable bare error={serviceError} onRetry={() => fetchKnowledgeBases()} />;
    }
    if (integrationError) {
      return (
        <div className="p-5 sm:p-6">
          <div role="alert" className="alert alert-warning rounded-xl text-sm font-semibold">
            <AlertTriangle size={18} className="shrink-0" />
            <span>{integrationError}</span>
          </div>
        </div>
      );
    }
    if (filteredKnowledgeBases.length === 0) {
      return search
        ? renderEmptyState(
          <BookOpen size={28} />,
          'No matching knowledge bases',
          'Try adjusting your search query or reset filters.',
          <button
            type="button"
            onClick={handleClearFilters}
            className="btn btn-sm btn-ghost border border-base-content/10 rounded-xl font-bold text-xs"
          >
            Clear all filters
          </button>
        )
        : renderEmptyState(
          <BookOpen size={28} />,
          'No knowledge bases found',
          canCreateKnowledgeBase
            ? `Get started by creating the first knowledge base for this ${ACCOUNT_LABEL.singularLower}.`
            : `This ${ACCOUNT_LABEL.singularLower} has no knowledge bases yet.`,
          canCreateKnowledgeBase ? (
            <button
              type="button"
              onClick={handleNew}
              className="btn btn-sm btn-primary rounded-xl font-bold gap-2 text-xs"
            >
              <PlusCircle size={14} />
              <span>Create Knowledge Base</span>
            </button>
          ) : null
        );
    }
    return (
      <div className="overflow-x-auto">
        <table className="table w-full text-left">
          <thead>
            <tr className="bg-base-200/40 border-b border-base-content/5 text-[10px] font-black uppercase tracking-widest text-base-content/40">
              <th className="py-3.5 px-5">Name</th>
              <th className="py-3.5 px-4">Description</th>
              <th className="py-3.5 px-4">Created Date</th>
              {canDeleteKnowledgeBase && <th className="py-3.5 px-5 text-right">Actions</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-base-content/5 text-xs">
            {filteredKnowledgeBases.map((kb) => (
              <tr key={kb.id} className="hover:bg-base-200/40 transition-colors group">
                <td className="py-3.5 px-5">
                  <button
                    type="button"
                    onClick={() => handleViewDocuments(kb)}
                    className="text-primary hover:underline font-bold text-sm tracking-tight text-left"
                  >
                    {kb.name || '—'}
                  </button>
                </td>
                <td className="py-3.5 px-4 max-w-md">
                  <span className="text-base-content/70 font-medium line-clamp-2">{kb.description || '—'}</span>
                </td>
                <td className="py-3.5 px-4 text-base-content/70 font-medium whitespace-nowrap">
                  {parseTimestamp(kb.created_at || kb.createdAt) ? (
                    <div
                      className="tooltip tooltip-top tooltip-primary before:text-xs before:font-semibold before:whitespace-nowrap"
                      data-tip={formatDateTime(kb.created_at || kb.createdAt)}
                    >
                      <span className="cursor-default hover:text-base-content transition-colors">
                        {formatDate(kb.created_at || kb.createdAt)}
                      </span>
                    </div>
                  ) : (
                    '—'
                  )}
                </td>
                {canDeleteKnowledgeBase && (
                  <td className="py-3.5 px-5 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <div className="tooltip tooltip-left tooltip-error before:text-xs before:font-semibold before:whitespace-nowrap" data-tip="Delete">
                        <button
                          type="button"
                          onClick={() => setDeleteTarget(kb)}
                          className="p-1.5 text-base-content/40 hover:text-error hover:bg-error/10 rounded-lg transition-all"
                          aria-label="Delete Knowledge Base"
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="w-full space-y-4 pb-2">
      {/* Hero Header */}
      <div className="glass-card rounded-2xl p-4 sm:p-5 shadow-sm border border-base-content/10">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="space-y-1">
            <h1 className="text-2xl sm:text-3xl font-black text-base-content tracking-tight">
              Knowledge Bases
            </h1>
            <p className="text-xs text-base-content/50">
              Knowledge bases available to the {ACCOUNT_LABEL.singularLower}'s AI Agent.
            </p>
          </div>

          {canCreateKnowledgeBase && (
            <div className="flex items-center gap-2.5 flex-wrap">
              <button
                type="button"
                onClick={handleNew}
                disabled={!canCreate}
                className="btn btn-sm btn-primary rounded-xl gap-2 font-bold shadow-sm hover:shadow transition-all"
              >
                <PlusCircle size={15} />
                <span>New Knowledge Base</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Filter & Search Bar Card */}
      <div className="glass-card rounded-2xl p-4 shadow-sm border border-base-content/10 space-y-3 relative z-30">
        <div className="grid grid-cols-12 gap-3">
          {/* Search Input */}
          <div className={`relative col-span-12 ${showAccountField ? 'sm:col-span-8 lg:col-span-9' : ''}`}>
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-base-content/40" size={16} />
            <input
              type="text"
              placeholder="Search by name or description..."
              disabled={needsAccount || Boolean(integrationError) || Boolean(serviceError)}
              className="w-full pl-10 pr-9 py-2.5 bg-base-100 border border-base-300 rounded-xl text-xs sm:text-sm text-base-content placeholder:text-base-content/30 focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all disabled:opacity-50"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-base-content/30 hover:text-base-content"
              >
                <X size={14} />
              </button>
            )}
          </div>

          {/* Account Filter (platform-wide view only) */}
          {showAccountField && (
            <div className="col-span-12 sm:col-span-4 lg:col-span-3">
              <select
                aria-label={ACCOUNT_LABEL.singular}
                className="w-full px-3 py-2.5 bg-base-100 border border-base-300 rounded-xl text-xs sm:text-sm text-base-content focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all font-medium"
                value={selectedAccountId}
                onChange={(e) => handleAccountChange(e.target.value)}
              >
                <option value="">— Select {ACCOUNT_LABEL.withArticle} —</option>
                {accounts.map(acc => (
                  <option key={acc.id} value={acc.id}>{acc.name}</option>
                ))}
              </select>
            </div>
          )}
        </div>

        {/* Active Filter Badges Bar */}
        {hasActiveFilters && (
          <div className="flex items-center justify-between gap-2 pt-2 border-t border-base-content/5 flex-wrap">
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="text-[10px] font-black uppercase tracking-wider text-base-content/40 flex items-center gap-1 mr-1">
                <Filter size={11} />
                <span>Active:</span>
              </span>

              {search && (
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-base-200 text-base-content/70 border border-base-content/5">
                  <span>Search: "{search}"</span>
                  <button type="button" onClick={() => setSearch('')} className="hover:text-error">
                    <X size={12} />
                  </button>
                </span>
              )}

              {showAccountField && selectedAccountId && (
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-base-200 text-base-content/70 border border-base-content/5">
                  <Building2 size={10} className="text-primary" />
                  <span>{ACCOUNT_LABEL.singular}: {selectedAccountName}</span>
                  <button type="button" onClick={() => handleAccountChange('')} className="hover:text-error">
                    <X size={12} />
                  </button>
                </span>
              )}
            </div>

            <button
              type="button"
              onClick={handleClearFilters}
              className="text-xs font-bold text-base-content/40 hover:text-error transition-colors ml-auto"
            >
              Clear all
            </button>
          </div>
        )}
      </div>

      {/* Main Table Card */}
      <div className="glass-card rounded-2xl shadow-sm border border-base-content/10 overflow-hidden p-0 relative z-10">
        {renderBody()}

        {!loading && !integrationError && !serviceError && filteredKnowledgeBases.length > 0 && (
          <div className="px-4 py-2.5 border-t border-base-content/5 text-xs text-base-content/60 font-medium">
            <span className="font-semibold text-base-content">{filteredKnowledgeBases.length}</span>
            {search ? <> of <span className="font-semibold text-base-content">{knowledgeBases.length}</span></> : null} knowledge bases
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={!!deleteTarget}
        onClose={() => { if (!deleting) { setDeleteTarget(null); } }}
        title="Delete Knowledge Base"
      >
        <div className="mb-6 space-y-3">
          <p className="text-base-content/70 text-sm leading-relaxed">
            Are you sure you want to delete{' '}
            <strong className="text-base-content font-mono px-1.5 py-0.5 rounded bg-base-200">
              {deleteTarget?.name}
            </strong>?
          </p>
          <div className="p-3 rounded-xl bg-error/10 border border-error/20 text-error text-xs font-semibold">
            This permanently removes the knowledge base from the AI Agent. This action cannot be undone.
          </div>
        </div>
        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => setDeleteTarget(null)}
            className="flex-1 px-4 py-2.5 border border-base-content/20 text-base-content/70 rounded-xl font-bold text-sm hover:bg-base-200 transition"
            disabled={deleting}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleDeleteConfirm}
            disabled={deleting}
            className="flex-1 px-4 py-2.5 bg-error text-error-content rounded-xl font-bold text-sm hover:opacity-90 transition disabled:opacity-50"
          >
            {deleting ? 'Deleting...' : 'Delete Knowledge Base'}
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default KnowledgeBases;
